import {
  endOfMonth,
  format,
  outputDateToApi,
  parseISO
} from '@sequencehq/utils/dates'

const START_YEAR = 2023
const CURRENT_YEAR = new Date().getFullYear()
const END_YEAR = CURRENT_YEAR + 1

const AVAILABLE_YEARS = Array.from(
  { length: END_YEAR - START_YEAR + 1 },
  (_, index) => String(START_YEAR + index)
)

export type Month = {
  label: string // 'MMM yyyy'
  start: string // YYYY-MM-01
  end: string // YYYY-MM-{lastDay}
}

export const MONTHS_ASC: Month[] = AVAILABLE_YEARS.flatMap(year =>
  Array.from({ length: 12 }, (_, index) => index + 1).map(month => {
    const startDate = parseISO(
      `${year}-${month.toString().padStart(2, '0')}-01`
    )
    return {
      label: format(startDate, 'MMM yyyy'),
      start: outputDateToApi(startDate),
      end: outputDateToApi(endOfMonth(startDate))
    }
  })
)

export const MONTH_START_OPTIONS = MONTHS_ASC.map(({ label, start }) => ({
  label,
  value: start
})).reverse()

export const MONTH_END_OPTIONS = MONTHS_ASC.map(({ label, end }) => ({
  label,
  value: end
})).reverse()

// Year-month key (YYYY-MM) lets us compare and look up dates regardless of
// whether they're stored as start-of-month (bookedFrom) or end-of-month
// (bookedTo, recognizedUpTo).
const toMonthKey = (value: string) => format(parseISO(value), 'yyyy-MM')

export const findMonth = (value: string): Month | undefined =>
  MONTHS_ASC.find(month => toMonthKey(month.start) === toMonthKey(value))

export const monthStartOf = (value: string): string =>
  findMonth(value)?.start ?? MONTHS_ASC[0].start

export const monthEndOf = (value: string): string =>
  findMonth(value)?.end ?? MONTHS_ASC[0].end

// Snap bookedFrom back when bookedTo moves to an earlier month. Returns the
// start of the month before the given value, or the same month if at the
// earliest available month.
export const previousMonthStartFrom = (value: string): string => {
  const index = MONTHS_ASC.findIndex(
    month => toMonthKey(month.start) === toMonthKey(value)
  )
  return MONTHS_ASC[Math.max(0, index - 1)].start
}

export const isBeforeMonth = (first: string, second: string) =>
  toMonthKey(first) < toMonthKey(second)
